import _ from 'lodash';
import { AgentStatusSettings, AgentStatus } from '@/types';
import { AGENT_STATUS_API, DEFAULT_STATUS } from '@/constants';

export const CURRENT_VERSION = '2';
export const getDefaultStatusOptions = () => {
  const statuses = Object.values(AGENT_STATUS_API);
  const options: AgentStatus[] = [];

  for (let i = 0; i < statuses.length; i++) {
    options.push({
      key: AGENT_STATUS_API[statuses[i]].toLowerCase(),
      status: statuses[i],
      order: i,
      canToggle: ['on', 'off'],
      isBasic: true,
    });
  }

  return options;
};

/**
 * Migrate existing settings or return default settings.
 * Can be called on every "GET" response.
 */
export const migrateToV2Settings = (settings: AgentStatusSettings) => {
  if (settings.version === CURRENT_VERSION) {
    return settings;
  }

  const { AVAILABLE, BUSY, OFFLINE } = AGENT_STATUS_API;
  const {
    busyStatusReasons = [],
    defaultLoginStatus = DEFAULT_STATUS,
    defaultLoginStatusReason = '',
  } = settings;

  const DEFAULT_OPTIONS = getDefaultStatusOptions();
  const statuses = [...DEFAULT_OPTIONS];
  let defaultStatusKey = '';

  /* If settings have one of the basic statuses as a default */
  const isBasicStatus = [AVAILABLE, OFFLINE].includes(defaultLoginStatus);
  const isBusyStatus = defaultLoginStatus === BUSY;
  const isBusyWithEmptyReason = isBusyStatus && !defaultLoginStatusReason;
  if (isBasicStatus || isBusyWithEmptyReason) {
    defaultStatusKey = defaultLoginStatus.toLowerCase();
  }

  for (let i = 0; i < busyStatusReasons.length; i++) {
    const reason = busyStatusReasons[i].replace(/\s\s+/g, '').trim();
    const key = _.kebabCase(`${AGENT_STATUS_API.BUSY}-${reason}`);
    /* Duplicate reasons receive unique keys with the same label */
    const isUnique = checkIsKeyUnique({ key, items: statuses });

    const status = {
      key: isUnique ? key : `${key}-${i}`,
      status: BUSY,
      label: reason,
      /* By default basic statuses are above custom */
      order: i + DEFAULT_OPTIONS.length,
      canToggle: ['on' as const, 'off' as const],
    };

    /* If user is using custom status as a default - use the generated key */
    statuses.push(status);
    if (isBusyStatus && reason === defaultLoginStatusReason) {
      defaultStatusKey = key;
    }
  }

  /* When creating new record legacy properties will not be added */
  const result: AgentStatusSettings = {
    version: CURRENT_VERSION,
    statuses: _.keyBy(statuses, 'key'),
    defaultStatusKey,
    busyStatusReasons,
    defaultLoginStatus,
    defaultLoginStatusReason,
  };
  return _.pickBy(result, value => !_.isEmpty(value));
};

/** Items with duplicate keys will be ignored */
export const checkIsKeyUnique = (args: {
  key: string;
  newKey?: string;
  items: AgentStatus[] | Record<string, AgentStatus>;
}) => {
  const { key, newKey } = args;
  const items = _.isArray(args.items) ? _.keyBy(args.items, 'key') : args.items;

  switch (true) {
    /* Main case is to search for an item with a key */
    case !newKey:
      return !items[key];
    /* When trying to rename key to a same value */
    case key === newKey:
      return true;
    /* Check if we can rename a key to a different value */
    default:
      return !items[newKey!];
  }
};
